import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatError } from '../lib/api';

// Module-level dedupe store — survives component unmount/remount so React
// StrictMode's double-invocation in dev doesn't try to exchange the same
// single-use Google auth code twice (which always fails with invalid_grant).
const PROCESSED_CODES = new Set();
// Remember the returnTo for each in-flight code. sessionStorage gets cleared
// by the first mount, so the StrictMode second mount needs this fallback to
// navigate the user back to the originating page (e.g. /premium) instead of '/'.
const RETURN_TO_BY_CODE = new Map();

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleAuthCallback() {
  const { loginWithGoogleCode, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const errParam = params.get('error');
    const state = params.get('state');
    const expected = sessionStorage.getItem('google_auth_state');
    // Prefer the cached value over sessionStorage because the first mount
    // already cleared sessionStorage. Fall back to sessionStorage for the
    // very first mount, then finally '/' if we have nothing.
    const returnTo =
      (code && RETURN_TO_BY_CODE.get(code)) ||
      sessionStorage.getItem('google_auth_return_to') ||
      '/';
    if (code) RETURN_TO_BY_CODE.set(code, returnTo);

    if (errParam) {
      sessionStorage.removeItem('google_auth_state');
      sessionStorage.removeItem('google_auth_return_to');
      setError('Google sign-in was cancelled.');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
      return;
    }
    if (!code) {
      sessionStorage.removeItem('google_auth_state');
      sessionStorage.removeItem('google_auth_return_to');
      setError('Missing authorization code.');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
      return;
    }

    // StrictMode dedupe: if another mount of this component already
    // started/finished processing this code, skip re-exchange. If the user is
    // already signed in (first exchange completed), just navigate home.
    if (PROCESSED_CODES.has(code)) {
      if (user) {
        sessionStorage.removeItem('google_auth_state');
        sessionStorage.removeItem('google_auth_return_to');
        navigate(returnTo, { replace: true });
      }
      // else: first exchange is still in-flight; the other mount will navigate.
      return;
    }

    if (expected && state !== expected) {
      sessionStorage.removeItem('google_auth_state');
      sessionStorage.removeItem('google_auth_return_to');
      setError('Sign-in state mismatch. Please try again.');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
      return;
    }

    PROCESSED_CODES.add(code);
    sessionStorage.removeItem('google_auth_state');
    sessionStorage.removeItem('google_auth_return_to');

    (async () => {
      try {
        const redirectUri = window.location.origin + '/auth/google';
        await loginWithGoogleCode(code, redirectUri);
        navigate(returnTo, { replace: true });
      } catch (err) {
        setError(formatError(err.response?.data?.detail) || 'Sign-in failed. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    })();
  }, [loginWithGoogleCode, navigate, user]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
      <div className="text-center" data-testid="google-auth-callback">
        {error ? (
          <>
            <p className="text-lg font-semibold mb-2" style={{ color: '#DC2626', fontFamily: 'Manrope' }}>
              {error}
            </p>
            <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>
              Redirecting you back…
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin" style={{ color: 'var(--primary)' }} />
            <p className="text-lg font-semibold" style={{ color: 'var(--text)', fontFamily: 'Manrope' }}>
              Signing you in…
            </p>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-fg)' }}>
              Hang tight while we finish setting things up.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
